import { TrendingUp, TrendingDown, Minus, Wallet, ClipboardCheck, Receipt, Users, Coins } from 'lucide-react';
import { fmtKc, barvaFleku } from './flekyGrid';

const ACCENT = '#3B82F6';

/**
 * Kanály platby a jejich barvy — **shodné s cockpitem** (`MojePokladnaPanel`
 * → `RozpadTrzby`). Manažer vidí tenhle pruh na telefonu u své směny; kdyby
 * měl ve Financích jiné barvy, četl by dvě různá čísla jako jedno.
 */
const KANALY = [
    { k: 'hotovost', label: 'Hotově', barva: '#F59E0B' },
    { k: 'karta', label: 'Kartou', barva: '#3B82F6' },
    { k: 'online', label: 'Online', barva: '#0CC0DF' },
];

/**
 * Stálá karta vpravo u fleku — protějšek `MaterialStatistika` ze Spižírny
 * a drží její rytmus: kruh s hlavním číslem, graf, rozpad 2×2, info dlaždice.
 *
 * Kruh u suroviny ukazuje, kde dnešní cena leží mezi svým minimem a maximem
 * („jsme na stropě, nebo na dně?"). U pokladny je ta otázka stejně praktická,
 * jen jinak položená: kde je dnešní hotovost mezi svým minimem a maximem za
 * čtrnáct dní. Plný šuplík je signál k odvodu do banky, prázdný k doplnění
 * základu — a ani jedno se z absolutní částky nepozná.
 *
 * Tržba stojí schválně **pod** hotovostí a odděleně. Tržba není stav
 * hotovosti: karta a online do šuplíku nikdy nedorazí. Většina omylů v tomhle
 * modulu vznikla z toho, že ta dvě čísla stála vedle sebe jako rovnocenná.
 */
export default function FlekStatistika({
    statistika, flek, isDark, cardStyle, mutedColor, secondaryColor, onAudit,
}) {
    if (!statistika || !flek) return null;
    const s = statistika;

    const trackColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const tileBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const tileBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const sekceBarva = barvaFleku(flek);

    const historie = (s.hotovost_14dni ?? []).map(d => Number(d.kc) || 0);
    const min = historie.length ? Math.min(...historie) : 0;
    const max = historie.length ? Math.max(...historie) : 0;
    const hotovost = Number(s.hotovost_kc) || 0;
    const zaklad = Number(s.pocatecni_kc) || 0;

    // Kruh: kde dnešní hotovost leží mezi minimem a maximem čtrnácti dnů.
    const pozice = max > min ? (hotovost - min) / (max - min) : null;
    const size = 190, sw = 26, R = (size - sw) / 2, C = 2 * Math.PI * R, cx = size / 2;
    const vyplnena = pozice == null ? 0 : Math.max(0.02, pozice) * C;

    const zmena = hotovost - zaklad;
    const Sipka = zmena === 0 ? Minus : zmena > 0 ? TrendingUp : TrendingDown;
    // Nad základ = vybráno, pod základ = ubylo. U pokladny je růst dobrá zpráva,
    // proto opačně než u ceny suroviny.
    const barvaZmeny = zmena === 0 ? mutedColor : zmena > 0 ? '#10B981' : '#EF4444';
    const barvaKruhu = pozice == null ? sekceBarva : pozice > 0.75 ? '#F59E0B' : pozice < 0.25 ? '#3B82F6' : sekceBarva;

    const trzba = s.trzba_dnes ?? {};
    const trzbaCelkem = Number(trzba.celkem ?? 0);
    const nenulove = KANALY.filter(x => Number(trzba[x.k] || 0) > 0);

    const audit = s.posledni_audit ?? null;
    const rozdil = audit ? Number(audit.rozdil_kc) : null;

    const infoTile = (Icon, value, label, zvyraznit = false) => (
        <div className="rounded-lg py-2.5 flex flex-col items-center gap-1" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
            <Icon size={15} style={{ color: zvyraznit ? sekceBarva : mutedColor }} />
            <div className="text-sm font-bold leading-none" style={{ color: 'var(--text-primary)' }}>{value}</div>
            <div className="text-[9px] text-center" style={{ color: mutedColor }}>{label}</div>
        </div>
    );

    return (
        <div className="rounded-xl flex flex-col" style={{ ...cardStyle, flexShrink: 0 }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: secondaryColor }}>Hotovost a tržba</span>
                {s.mesic?.smen != null && (
                    <span className="text-[10px]" style={{ color: mutedColor }}>{s.mesic.smen} směn v měsíci</span>
                )}
            </div>

            <div className="p-4">
                {/* Kruh — kde dnešní hotovost leží ve svém rozpětí */}
                <div className="relative" style={{ width: size, height: size, margin: '0 auto' }}>
                    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx={cx} cy={cx} r={R} fill="none" stroke={trackColor} strokeWidth={sw} />
                        {pozice != null && (
                            <circle cx={cx} cy={cx} r={R} fill="none" stroke={barvaKruhu} strokeWidth={sw} strokeLinecap="butt"
                                strokeDasharray={`${vyplnena} ${C - vyplnena}`}
                                style={{ transition: 'stroke-dasharray 0.4s' }} />
                        )}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="text-[26px] font-bold leading-none tabular-nums" style={{ color: 'var(--text-primary)' }}>
                            {new Intl.NumberFormat('cs-CZ').format(Math.round(hotovost))}
                        </div>
                        <div className="text-[11px] mt-1" style={{ color: mutedColor }}>Kč v šuplíku</div>
                        {zmena !== 0 && (
                            <div className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: `${barvaZmeny}1f`, color: barvaZmeny }}>
                                <Sipka size={10} />{zmena > 0 ? '+' : ''}{new Intl.NumberFormat('cs-CZ').format(Math.round(zmena))} nad základ
                            </div>
                        )}
                    </div>
                </div>

                {pozice != null && (
                    <p className="text-[10px] text-center mt-2" style={{ color: mutedColor }}>
                        {pozice > 0.75 ? 'Nejvíc hotovosti za čtrnáct dní — čas na odvod do banky'
                            : pozice < 0.25 ? 'Nejmíň hotovosti za čtrnáct dní'
                                : 'Hotovost je uprostřed svého rozpětí'}
                    </p>
                )}

                {/* Tržba dnes — pruh a legenda jako v cockpitu */}
                <div className="mt-4 rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                    <div className="flex items-baseline justify-between mb-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>Tržba dnes</span>
                        <span className="text-[13px] font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmtKc(trzbaCelkem)}</span>
                    </div>
                    {trzbaCelkem > 0 ? (
                        <>
                            <div aria-hidden="true" style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                {nenulove.map(x => (
                                    <span key={x.k} style={{ flex: Number(trzba[x.k]), background: x.barva }} />
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 7 }}>
                                {KANALY.map(x => (
                                    <span key={x.k} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: mutedColor }}>
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: x.barva, flexShrink: 0 }} />
                                            {x.label}
                                        </span>
                                        <span className="tabular-nums" style={{
                                            fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>{fmtKc(trzba[x.k])}</span>
                                    </span>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-[11px]" style={{ color: mutedColor }}>Dnes tudy zatím nic neprošlo.</p>
                    )}
                </div>

                {/* Vývoj hotovosti — sloupce, ne křivka: pohyby jsou skokové */}
                <div className="mt-3 rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: mutedColor }}>Vývoj hotovosti</div>
                    {historie.length <= 1 ? (
                        <p className="text-[11px]" style={{ color: mutedColor }}>Historie zatím není z čeho kreslit.</p>
                    ) : (
                        <>
                            <div className="flex items-end gap-1" style={{ height: 56 }}>
                                {(s.hotovost_14dni ?? []).map((d, i) => {
                                    const v = Number(d.kc) || 0;
                                    const posledni = i === s.hotovost_14dni.length - 1;
                                    return (
                                        <div key={d.datum ?? i} className="flex-1 rounded-t" title={`${d.datum}: ${fmtKc(v)}`}
                                            style={{
                                                height: `${Math.max(8, (v / (max || 1)) * 100)}%`,
                                                background: posledni ? sekceBarva : (isDark ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.16)'),
                                                minWidth: 6, transition: 'height 0.3s',
                                            }} />
                                    );
                                })}
                            </div>
                            <div className="flex items-center justify-between mt-1.5 text-[10px]" style={{ color: mutedColor }}>
                                <span>před 14 dny</span><span>dnes</span>
                            </div>
                        </>
                    )}
                </div>

                {/* Rozpad 2×2 — rozpětí hotovosti */}
                {historie.length > 1 && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                            <div className="text-[11px] mb-1" style={{ color: secondaryColor }}>Nejmíň</div>
                            <div className="text-lg font-bold leading-none tabular-nums" style={{ color: '#3B82F6' }}>
                                {new Intl.NumberFormat('cs-CZ').format(Math.round(min))} <span className="text-[11px] font-normal" style={{ color: mutedColor }}>Kč</span>
                            </div>
                        </div>
                        <div className="rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                            <div className="text-[11px] mb-1" style={{ color: secondaryColor }}>Nejvíc</div>
                            <div className="text-lg font-bold leading-none tabular-nums" style={{ color: '#F59E0B' }}>
                                {new Intl.NumberFormat('cs-CZ').format(Math.round(max))} <span className="text-[11px] font-normal" style={{ color: mutedColor }}>Kč</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Info dlaždice */}
                {s.mesic && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        {infoTile(Users, s.mesic.smen, s.mesic.smen === 1 ? 'směna' : 'směn', s.mesic.smen > 0)}
                        {infoTile(Receipt, fmtKc(s.mesic.prumerna_trzba_kc), 'Ø tržba', s.mesic.prumerna_trzba_kc > 0)}
                        {infoTile(Coins, fmtKc(s.mesic.dysko_kc), 'dýška', s.mesic.dysko_kc > 0)}
                        {infoTile(Wallet, fmtKc(s.mesic.manko_kc), 'manka', s.mesic.manko_kc > 0)}
                    </div>
                )}

                {/* Poslední audit — protějšek tlačítka „Zobrazit a nahradit" */}
                <button onClick={onAudit}
                    className="flex items-center justify-between w-full mt-3 py-2.5 px-3 rounded-lg text-xs font-semibold"
                    style={{
                        background: audit ? tileBg : 'none',
                        border: `1px ${audit ? 'solid' : 'dashed'} ${tileBorder}`,
                        color: 'var(--text-primary)', cursor: 'pointer',
                    }}>
                    <span className="flex items-center gap-1.5" style={{ color: audit ? mutedColor : '#F59E0B' }}>
                        <ClipboardCheck size={13} />
                        {audit ? `Audit ${audit.datum}` : 'Flek ještě nikdo neauditoval'}
                    </span>
                    {audit && (
                        <span className="tabular-nums font-bold"
                            style={{ color: rozdil === 0 ? '#10B981' : (rozdil > 0 ? '#F59E0B' : '#EF4444') }}>
                            {rozdil > 0 ? '+' : ''}{fmtKc(rozdil)}
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}
